import { useEffect, useState } from "react";
import api from "../api";
import { ApptModal, IcloudEventModal, ProposalModal, GoogleEventModal } from "../components/CalendarModals";
import ReviewRecovery from "../components/ReviewRecovery";
import ApptOrigin from "../components/ApptOrigin";

const MODES = {
  zoom: { lbl: "Videoriunione Zoom", short: "Zoom", color: "#2563eb" },
  presenza: { lbl: "Riunione in presenza", short: "In studio", color: "#b45309" },
  trasferta: { lbl: "Trasferta dal cliente", short: "Trasferta", color: "#7c3aed" },
  phone: { lbl: "Telefonata", short: "Telefono", color: "#16a34a" },
  studio: { lbl: "Lavoro di studio", short: "Studio", color: "#0ea5e9" },
  scelta: { lbl: "Sceglie il cliente", short: "A scelta", color: "#64748b" },
};
const STATUS = {
  in_attesa: { lbl: "In attesa", cls: "in_attesa" },
  confermato: { lbl: "Confermato", cls: "gestita" },
  controproposta: { lbl: "Controproposta", cls: "in_attesa" },
  rifiutato: { lbl: "Rifiutato", cls: "da_gestire" },
};
const MESI = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];
const GIORNI_FULL = ["Domenica", "Lunedì", "Martedì", "Mercoledì", "Giovedì", "Venerdì", "Sabato"];
const startOfDay = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };

const dkey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const itD = (s) => { if (!s) return "—"; const p = String(s).slice(0, 10).split("-"); return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : s; };

export default function Calendar() {
  const [appts, setAppts] = useState([]);
  const [cfg, setCfg] = useState(null);
  const [weekStart, setWeekStart] = useState(startOfDay(new Date()));
  const [view, setView] = useState("mese");
  const [sendMode, setSendMode] = useState("scelta");
  const [propose, setPropose] = useState(null); // appt under counterproposal
  const [propDate, setPropDate] = useState("");
  const [propTime, setPropTime] = useState("");
  const [companies, setCompanies] = useState([]);
  const [leads, setLeads] = useState([]);
  const [newAppt, setNewAppt] = useState(false);
  const [editContact, setEditContact] = useState(null);
  const [gcalConnected, setGcalConnected] = useState(false);
  const [origin, setOrigin] = useState(null); // appuntamento di cui mostrare la provenienza
  const [gevents, setGevents] = useState([]);
  const [icalConnected, setIcalConnected] = useState(false);
  const [icevents, setIcevents] = useState([]);
  const [icevent, setIcevent] = useState(null);
  const [gevent, setGevent] = useState(null);
  const [reviewsOpen, setReviewsOpen] = useState(false);
  const [gform, setGform] = useState({ summary: "", description: "", date: "", start: "", end: "", location: "" });
  const [editId, setEditId] = useState(null);
  const [dups, setDups] = useState([]);
  const emptyForm = { category: "cliente", date: "", time: "", dur: 30, mode: "studio", company_id: "", company: "", title: "", gcal: false, end_date: "", all_day: false, blocks_booking: true, count_hours: true, person_id: "", lead_new: false, lead_first: "", lead_last: "", lead_email: "", lead_phone: "", send_sms_reminder: false, sms_phone: "", with_who: "", participants: [] };
  const [form, setForm] = useState(emptyForm);

  const loadLeads = () => api.get("/persons?kind=lead").then((r) => setLeads(r.data)).catch(() => setLeads([]));

  const load = () => {
    api.get("/appointments").then((r) => setAppts(r.data)).catch(() => {});
  };
  const loadDups = () => api.get("/appointments/duplicates").then((r) => setDups(r.data)).catch(() => setDups([]));
  useEffect(() => {
    load();
    loadDups();
    api.get("/booking-config").then((r) => { setCfg(r.data); setSendMode(r.data.default_send || "scelta"); }).catch(() => {});
    api.get("/companies").then((r) => setCompanies(r.data)).catch(() => {});
    loadLeads();
    api.get("/calendar/status").then((r) => setGcalConnected(r.data.connected)).catch(() => {});
    api.get("/icloud/status").then((r) => setIcalConnected(r.data.connected)).catch(() => {});
  }, []);

  const closeAndReload = () => { setNewAppt(false); setEditId(null); setEditContact(null); setForm(emptyForm); load(); loadDups(); loadGcal(); };

  const createApptFromIcloud = (e) => {
    let dur = 30;
    if (!e.all_day && e.start && e.end) {
      const diff = (new Date(e.end) - new Date(e.start)) / 60000;
      if (diff > 0) dur = Math.round(diff);
    }
    setEditId(null);
    setForm({
      ...emptyForm, category: "cliente",
      date: (e.start || "").slice(0, 10),
      time: e.all_day ? "09:00" : (e.start || "").slice(11, 16),
      dur, title: e.summary || "", count_hours: true, gcal: false,
    });
    setIcevent(null);
    setNewAppt(true);
  };

  const createApptFromGcal = (e) => {
    let dur = 30;
    if (!e.all_day && e.start && e.end) {
      const diff = (new Date(e.end) - new Date(e.start)) / 60000;
      if (diff > 0) dur = Math.round(diff);
    }
    setEditId(null);
    setForm({
      ...emptyForm, category: "cliente",
      date: (e.start || "").slice(0, 10),
      time: e.all_day ? "09:00" : (e.start || "").slice(11, 16),
      dur, title: e.summary || "", count_hours: true, gcal: false,
      send_sms_reminder: true,
    });
    setGevent(null);
    setNewAppt(true);
  };

  const saveWithOverride = async (body) => {
    const send = (force) => editId
      ? api.put(`/appointments/${editId}${force ? "?force=1" : ""}`, body)
      : api.post(`/appointments${force ? "?force=1" : ""}`, body);
    try { await send(false); return true; }
    catch (e) {
      const det = e?.response?.data?.detail || "";
      if (e?.response?.status === 409 && det.includes("sovrappone")) {
        if (window.confirm(`⚠️ ${det}.\n\nVuoi salvarlo comunque?`)) {
          try { await send(true); return true; }
          catch (e2) { alert(e2?.response?.data?.detail || "Errore"); return false; }
        }
        return false;
      }
      alert(det || "Errore");
      return false;
    }
  };

  const saveAppt = async () => {
    const cat = form.category;
    // Periodo / chiusura su uno o più giorni (corso, ferie) — mai conteggiato
    if (cat === "blocco") {
      if (!form.date) { alert("Indica la data di inizio"); return; }
      if (!form.title) { alert("Indica una descrizione (es. Ferie, Corso)"); return; }
      try {
        const body = {
          category: "blocco", date: form.date, end_date: form.end_date || form.date,
          time: form.all_day ? "00:00" : (form.time || "00:00"),
          dur: form.all_day ? 1440 : (Number(form.dur) || 30),
          mode: "blocco", title: form.title, name: form.title, company_id: "", company: "",
          all_day: !!form.all_day, blocks_booking: !!form.blocks_booking, count_hours: false,
        };
        if (editId) await api.put(`/appointments/${editId}`, body);
        else await api.post("/appointments", body);
        closeAndReload();
      } catch (e) { alert(e?.response?.data?.detail || "Errore"); }
      return;
    }
    if (!form.date || !form.time) { alert("Indica data e ora"); return; }
    if (cat === "cliente" && !form.company_id) { alert("Seleziona il cliente"); return; }
    const cleanParticipants = (form.participants || [])
      .map((p) => ({ name: (p.name || "").trim(), email: (p.email || "").trim() || null, phone: (p.phone || "").trim() || null }))
      .filter((p) => p.name || p.email || p.phone);
    // Incontro con Lead: lead esistente o nuova anagrafica
    if (cat === "lead") {
      let pid = form.person_id;
      let leadName = "";
      if (form.lead_new) {
        if (!form.lead_first.trim() || !form.lead_last.trim()) { alert("Indica nome e cognome del lead"); return; }
        try {
          const r = await api.post("/persons", {
            first_name: form.lead_first.trim(), last_name: form.lead_last.trim(), kind: "lead",
            email: form.lead_email.trim() || null, phone: form.lead_phone.trim() || null,
          });
          pid = r.data.id; leadName = `${r.data.first_name} ${r.data.last_name}`.trim();
          loadLeads();
        } catch (e) { alert("Errore creazione anagrafica: " + (e?.response?.data?.detail || e.message)); return; }
      } else {
        if (!pid) { alert("Seleziona un lead o crea una nuova anagrafica"); return; }
        const lp = leads.find((p) => p.id === pid);
        leadName = lp ? `${lp.first_name} ${lp.last_name}`.trim() : "Lead";
      }
      const body = {
        category: "lead", date: form.date, time: form.time, dur: Number(form.dur) || 30,
        mode: form.mode, person_id: pid, company_id: "", company: "",
        name: leadName, title: form.title || "Incontro con Lead", count_hours: false,
        send_sms_reminder: !!form.send_sms_reminder,
        phone: form.send_sms_reminder ? (form.sms_phone || "").trim() : "",
        participants: cleanParticipants,
      };
      if (await saveWithOverride(body)) closeAndReload();
      return;
    }
    const comp = companies.find((c) => c.id === form.company_id);
    const compName = comp ? comp.name : "";
    const linkClient = cat === "cliente" || (cat === "studio" && form.company_id);
    const withWho = (form.with_who || "").trim();
    const displayName = cat === "personale" ? (form.title || "Personale")
      : cat === "studio" ? (withWho || compName || form.title || "Lavoro di studio") : (withWho || compName);
    const body = {
      category: cat, date: form.date, time: form.time, dur: Number(form.dur) || 30,
      mode: cat === "personale" ? "scelta" : form.mode,
      company_id: linkClient ? form.company_id : "",
      company: linkClient ? compName : "",
      name: displayName,
      title: form.title || (cat === "cliente" ? (MODES[form.mode]?.lbl || "") : ""),
      count_hours: cat === "personale" ? false : !!form.count_hours,
      send_sms_reminder: cat === "cliente" ? !!form.send_sms_reminder : false,
      phone: cat === "cliente" && form.send_sms_reminder ? (form.sms_phone || "").trim() : "",
      participants: cleanParticipants,
    };
    if (!(await saveWithOverride(body))) return;
    if (!editId && form.gcal && gcalConnected) {
      try {
        await api.post("/calendar/events", {
          summary: displayName + (cat === "cliente" ? ` — ${MODES[form.mode]?.short || ""}` : ""),
          description: form.title || "",
          start: `${form.date}T${form.time}:00`,
          duration_min: Number(form.dur) || 30,
        });
      } catch (e) { alert("Impegno salvato, ma evento Google non creato: " + (e?.response?.data?.detail || e.message)); }
    }
    closeAndReload();
  };

  const openEditAppt = (a) => {
    setEditId(a.id);
    if (a.email || a.phone) {
      const base = { email: a.email || "", phone: a.phone || "" };
      setEditContact(base);
      api.get(`/cambio-commercialista/lead-by-appt/${a.id}`)
        .then((r) => setEditContact({ ...base, ...r.data }))
        .catch(() => {});
    } else {
      setEditContact(null);
    }
    const cat = a.category || (a.mode === "blocco" || a.blocks_booking || a.end_date ? "blocco" : "cliente");
    if (cat === "blocco") {
      setForm({ ...emptyForm, category: "blocco", date: a.date, end_date: a.end_date || a.date, time: a.time || "00:00",
                dur: a.dur || 30, mode: "blocco", title: a.title || a.name || "", all_day: !!a.all_day,
                blocks_booking: a.blocks_booking !== false, count_hours: false });
    } else if (cat === "lead") {
      setForm({ ...emptyForm, category: "lead", date: a.date, time: a.time, dur: a.dur || 30,
                mode: a.mode || "presenza", person_id: a.person_id || "", title: a.title || "", count_hours: false,
                send_sms_reminder: !!a.send_sms_reminder, sms_phone: a.phone || "", participants: a.participants || [] });
    } else {
      let cid = a.company_id || "";
      if (!cid && a.company) { const mt = companies.find((c) => c.name === a.company); if (mt) cid = mt.id; }
      setForm({ ...emptyForm, category: cat, date: a.date, time: a.time, dur: a.dur || 30, mode: a.mode || "studio",
                company_id: cid, company: a.company || "", title: a.title || "", count_hours: a.count_hours !== false,
                with_who: a.name && a.name !== (a.company || "") ? a.name : "",
                send_sms_reminder: !!a.send_sms_reminder, sms_phone: a.phone || "", participants: a.participants || [] });
    }
    setNewAppt(true);
  };
  const openNewOnDate = (dateKey) => {
    setEditId(null);
    setEditContact(null);
    setForm({ ...emptyForm, date: dateKey });
    setNewAppt(true);
  };
  const deleteAppt = async () => {
    try { await api.delete(`/appointments/${editId}`); closeAndReload(); }
    catch (e) { alert(e?.response?.data?.detail || "Errore"); }
  };

  const isCurrentMonth = weekStart.getMonth() === new Date().getMonth() && weekStart.getFullYear() === new Date().getFullYear();

  const gridRange = () => {
    if (view === "settimana") {
      const s = startOfDay(weekStart); const e = new Date(s); e.setDate(s.getDate() + 7);
      return [s, e];
    }
    if (isCurrentMonth) {
      // parte dalla settimana in corso e mostra un mese pieno (5 settimane)
      const cw = startOfDay(new Date()); cw.setDate(cw.getDate() - ((cw.getDay() + 6) % 7));
      const e = new Date(cw); e.setDate(cw.getDate() + 35);
      return [cw, e];
    }
    const first = new Date(weekStart.getFullYear(), weekStart.getMonth(), 1);
    const start = new Date(first); start.setDate(first.getDate() - ((first.getDay() + 6) % 7));
    const last = new Date(weekStart.getFullYear(), weekStart.getMonth() + 1, 0);
    const end = new Date(last); end.setDate(last.getDate() + (7 - ((last.getDay() + 6) % 7)));
    return [startOfDay(start), startOfDay(end)];
  };

  const loadGcal = () => {
    const [gs, ge] = gridRange();
    const tmin = gs.toISOString();
    const tmax = ge.toISOString();
    // In caso di errore momentaneo (Google non risponde) NON svuotiamo gli eventi
    // già mostrati: riproviamo una volta dopo 4 secondi.
    if (gcalConnected) {
      api.get(`/calendar/events?time_min=${encodeURIComponent(tmin)}&time_max=${encodeURIComponent(tmax)}`)
        .then((r) => setGevents(r.data))
        .catch(() => {
          setTimeout(() => {
            api.get(`/calendar/events?time_min=${encodeURIComponent(tmin)}&time_max=${encodeURIComponent(tmax)}`)
              .then((r) => setGevents(r.data)).catch(() => {});
          }, 4000);
        });
    }
    if (icalConnected) {
      api.get(`/icloud/events?time_min=${encodeURIComponent(tmin)}&time_max=${encodeURIComponent(tmax)}`)
        .then((r) => setIcevents(r.data))
        .catch(() => {
          setTimeout(() => {
            api.get(`/icloud/events?time_min=${encodeURIComponent(tmin)}&time_max=${encodeURIComponent(tmax)}`)
              .then((r) => setIcevents(r.data)).catch(() => {});
          }, 4000);
        });
    }
  };

  const openGevent = (e) => {
    setGevent(e);
    setGform({
      summary: e.summary || "", description: e.description || "", location: e.location || "",
      date: (e.start || "").slice(0, 10),
      start: e.all_day ? "" : (e.start || "").slice(11, 16),
      end: e.all_day ? "" : (e.end || "").slice(11, 16),
    });
  };
  const saveGevent = async () => {
    if (!gevent.editable) { alert("Questo calendario è in sola lettura."); return; }
    const body = { summary: gform.summary, description: gform.description, location: gform.location };
    if (gform.date && gform.start) body.start = `${gform.date}T${gform.start}:00`;
    if (gform.date && gform.end) body.end = `${gform.date}T${gform.end}:00`;
    try {
      await api.put(`/calendar/events/${encodeURIComponent(gevent.calendar_id)}/${encodeURIComponent(gevent.id)}`, body);
      setGevent(null); loadGcal();
    } catch (e) { alert(e?.response?.data?.detail || e.message); }
  };
  const deleteGevent = async () => {
    try {
      await api.delete(`/calendar/events/${encodeURIComponent(gevent.calendar_id)}/${encodeURIComponent(gevent.id)}`);
      setGevent(null); loadGcal();
    } catch (e) { alert(e?.response?.data?.detail || e.message); }
  };

  const pending = appts.filter((a) => a.status === "in_attesa");
  const confirmed = appts.filter((a) => a.status === "confermato" && a.category !== "personale" && (a.end_date || a.date) >= dkey(new Date())).sort((a, b) => (a.date + a.time).localeCompare(b.date + b.time));

  useEffect(() => { loadGcal(); /* eslint-disable-next-line */ }, [gcalConnected, icalConnected, weekStart, view]);

  const accept = (id) => {
    api.post(`/appointments/${id}/accept`).then(() => load())
      .catch((e) => alert(e?.response?.data?.detail || "Errore"));
  };

  const reject = (a) => {
    if (!window.confirm(`Rifiutare la richiesta di ${a.name || "questo contatto"}? Resta nello storico come rifiutata, ma non occupa più l'agenda.`)) return;
    api.put(`/appointments/${a.id}`, { status: "rifiutato" }).then(() => load())
      .catch((e) => alert(e?.response?.data?.detail || "Errore"));
  };

  const removeRequest = (a) => {
    if (!window.confirm(`Eliminare definitivamente la richiesta di ${a.name || "questo contatto"}? Non sarà più recuperabile.`)) return;
    api.delete(`/appointments/${a.id}`).then(() => load())
      .catch((e) => alert(e?.response?.data?.detail || "Errore"));
  };

  const sendProposal = () => {
    if (!propDate || !propTime) { alert("Indica data e ora della controproposta"); return; }
    api.post(`/appointments/${propose.id}/propose`, { proposed_date: propDate, proposed_time: propTime })
      .then(() => { setPropose(null); setPropDate(""); setPropTime(""); load(); })
      .catch((e) => alert(e?.response?.data?.detail || "Errore"));
  };

  const copyLink = async () => {
    const base = cfg?.booking_link || `${window.location.origin}/prenota`;
    const link = base + (base.includes("?") ? "&" : "?") + "modalita=" + sendMode;
    let copied = false;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(link);
        copied = true;
      }
    } catch (e) { copied = false; }
    if (!copied) {
      try {
        const ta = document.createElement("textarea");
        ta.value = link; ta.style.position = "fixed"; ta.style.opacity = "0";
        document.body.appendChild(ta); ta.focus(); ta.select();
        copied = document.execCommand("copy");
        document.body.removeChild(ta);
      } catch (e) { copied = false; }
    }
    alert((copied ? "Link prenotazione copiato:\n\n" : "Copia manualmente questo link:\n\n") + link);
  };

  // Vista settimanale — 7 giorni a partire da weekStart
  const weekDays = [];
  for (let i = 0; i < 7; i++) { const d = new Date(weekStart); d.setDate(weekStart.getDate() + i); weekDays.push(d); }
  const weekEnd = weekDays[6];
  const todayKey = dkey(new Date());
  const rangeLabel = weekStart.getMonth() === weekEnd.getMonth()
    ? `${weekStart.getDate()}–${weekEnd.getDate()} ${MESI[weekEnd.getMonth()]} ${weekEnd.getFullYear()}`
    : `${weekStart.getDate()} ${MESI[weekStart.getMonth()].slice(0, 3)} – ${weekEnd.getDate()} ${MESI[weekEnd.getMonth()].slice(0, 3)} ${weekEnd.getFullYear()}`;
  const wStartKey = dkey(weekStart), wEndKey = dkey(weekEnd);
  const weekAppts = appts.filter((a) => {
    if (a.status === "rifiutato") return false;
    if (a.mode === "blocco" || a.blocks_booking) return false;
    return a.date >= wStartKey && a.date <= wEndKey;
  });
  const weekHours = weekAppts.reduce((s, a) => s + (a.count_hours !== false ? (Number(a.dur) || 0) : 0), 0) / 60;
  const weekHoursLbl = weekHours > 0 ? (Number.isInteger(weekHours) ? `${weekHours}` : weekHours.toFixed(1).replace(".", ",")) : "0";

  // Tutti gli impegni di un giorno (CRM + Google + iCloud) ORDINATI PER ORARIO
  const dayItems = (k) => {
    const out = [];
    appts.forEach((a) => {
      if (a.status === "rifiutato") return;
      const end = a.end_date || a.date;
      if (a.date <= k && k <= end) out.push({ kind: "appt", time: a.time || "", a });
    });
    gevents.forEach((e) => { if ((e.start || "").slice(0, 10) === k) out.push({ kind: "gcal", time: e.all_day ? "" : (e.start || "").slice(11, 16), e }); });
    icevents.forEach((e) => { if ((e.start || "").slice(0, 10) === k) out.push({ kind: "icloud", time: e.all_day ? "" : (e.start || "").slice(11, 16), e }); });
    out.sort((x, y) => (x.time || "00:00").localeCompare(y.time || "00:00"));
    return out;
  };

  const navPeriod = (dir) => {
    const d = new Date(weekStart);
    if (view === "settimana") d.setDate(d.getDate() + 7 * dir);
    else d.setMonth(d.getMonth() + dir, 1);
    setWeekStart(startOfDay(d));
  };

  // Vista mensile — griglia con settimane complete (lun→dom)
  const [gridStart, gridEnd] = gridRange();
  const monthDays = [];
  if (view === "mese") {
    for (let d = new Date(gridStart); d < gridEnd; d.setDate(d.getDate() + 1)) monthDays.push(new Date(d));
  }
  const monthKeyStart = dkey(new Date(weekStart.getFullYear(), weekStart.getMonth(), 1));
  const monthKeyEnd = dkey(new Date(weekStart.getFullYear(), weekStart.getMonth() + 1, 0));
  const monthAppts = appts.filter((a) => {
    if (a.status === "rifiutato" || a.mode === "blocco" || a.blocks_booking) return false;
    return a.date >= monthKeyStart && a.date <= monthKeyEnd;
  });
  const monthHours = monthAppts.reduce((s, a) => s + (a.count_hours !== false ? (Number(a.dur) || 0) : 0), 0) / 60;
  const recapAppts = view === "mese" ? monthAppts : weekAppts;
  const recapHours = view === "mese" ? monthHours : weekHours;
  const recapHoursLbl = recapHours > 0 ? (Number.isInteger(recapHours) ? `${recapHours}` : recapHours.toFixed(1).replace(".", ",")) : "0";
  const gridEndIncl = new Date(gridEnd.getTime() - 86400000);
  const periodLabel = view === "mese"
    ? (isCurrentMonth && gridStart.getMonth() !== gridEndIncl.getMonth()
        ? `${MESI[gridStart.getMonth()]} – ${MESI[gridEndIncl.getMonth()]} ${gridEndIncl.getFullYear()}`
        : `${MESI[weekStart.getMonth()]} ${weekStart.getFullYear()}`)
    : rangeLabel;

  const renderItem = (it, i, compact) => {
    if (it.kind === "appt") {
      const a = it.a;
      if (a.mode === "blocco" || a.blocks_booking) {
        return (
          <div key={`a${a.id}-${i}`} className={compact ? "month-ev block" : "week-ev"} onClick={() => openEditAppt(a)}
            style={compact ? {} : { cursor: "pointer", background: "#475569", color: "#fff", borderLeft: "3px solid #f59e0b" }}
            title={a.title || a.name} data-testid="block-event">
            🔒 {a.title || a.name}{!compact && a.end_date && a.end_date !== a.date ? ` (${itD(a.date)}→${itD(a.end_date)})` : ""}
          </div>
        );
      }
      return compact ? (
        <div key={`a${a.id}-${i}`} className={"month-ev appt " + (a.status === "in_attesa" || a.status === "controproposta" ? "pending" : a.mode)}
          onClick={() => openEditAppt(a)} title={`${it.time} ${a.name}${a.company ? " · " + a.company : ""}`} data-testid="month-appt">
          <b>{it.time}</b> {a.company || a.name}
        </div>
      ) : (
        <div key={`a${a.id}-${i}`} className={"week-ev " + (a.status === "in_attesa" || a.status === "controproposta" ? "pending" : a.mode)} onClick={() => openEditAppt(a)} style={{ cursor: "pointer" }} data-testid="appt-event">
          <span className="week-ev-time">{a.time}</span> {a.client_confirmed ? "✅ " : ""}{a.name}{a.company ? <span style={{ opacity: .75 }}> · {a.company}</span> : ""}
        </div>
      );
    }
    if (it.kind === "gcal") {
      const e = it.e;
      return compact ? (
        <div key={`g${e.id}-${i}`} className="month-ev gcal" onClick={() => openGevent(e)} title={e.summary} data-testid="month-gcal"><b>{it.time || "G"}</b> {e.summary}</div>
      ) : (
        <div key={`g${e.id}-${i}`} className="week-ev gcal" onClick={() => openGevent(e)} style={{ cursor: "pointer" }} data-testid="gcal-event">
          <span className="week-ev-time">{e.all_day ? "Giorno" : it.time}</span> {e.summary}
        </div>
      );
    }
    const e = it.e;
    return compact ? (
      <div key={`i${e.id}-${i}`} className="month-ev ic" style={{ borderLeftColor: (e.color || "#94a3b8").slice(0, 7) }} onClick={() => setIcevent(e)} title={e.summary} data-testid="month-icloud"><b>{it.time || "G"}</b> {e.summary}</div>
    ) : (
      <div key={`i${e.id}-${i}`} className="week-ev" onClick={() => setIcevent(e)} style={{ cursor: "pointer", background: "#0f172a", color: "#fff", borderLeft: `3px solid ${(e.color || "#94a3b8").slice(0, 7)}` }} data-testid="icloud-event">
        <span className="week-ev-time">{e.all_day ? "Giorno" : it.time}</span> {e.summary}
      </div>
    );
  };

  return (
    <div>
      <h1 className="page-title">Calendario</h1>
      <p className="page-sub">Appuntamenti, prenotazioni e sincronizzazione</p>

      {/* Calendari sincronizzati */}
      <div className="panel">
        <h3>Calendari sincronizzati</h3>
        <div className="toolbar">
          <span className={"pill " + (gcalConnected ? "gestita" : "da_gestire")} data-testid="gcal-status">
            Google: {gcalConnected ? `${gevents.length} eventi` : "non collegato"}
          </span>
          <span className={"pill " + (icalConnected ? "gestita" : "da_gestire")} data-testid="icloud-status">
            iCloud: {icalConnected ? `${icevents.length} eventi` : "non collegato"}
          </span>
          {(gcalConnected || icalConnected) && <button className="btn sm" onClick={loadGcal} data-testid="cal-refresh">Aggiorna</button>}
          <button className="btn sm" onClick={() => setReviewsOpen(true)} data-testid="cal-reviews-btn">🌟 Recensioni</button>
          <div className="spacer" />
          <a className="btn sm" href="/impostazioni?tab=calendario" data-testid="cal-settings-link">Gestisci calendari ⚙</a>
        </div>
        {!gcalConnected && !icalConnected && <p className="muted">Collega Google e/o iCloud da Impostazioni → Calendario per vedere qui i tuoi eventi.</p>}
      </div>

      {/* Possibili doppioni */}
      {dups.length > 0 && (
        <div className="panel" data-testid="duplicates-panel" style={{ borderLeft: "3px solid #f59e0b" }}>
          <h3>Possibili appuntamenti doppi {<span className="nav-badge">{dups.length}</span>}</h3>
          <p className="muted">Stesso cliente nello stesso giorno e ora. Per evitare di duplicare le ore, modifica un doppione e togli la spunta "Conteggia queste ore" (oppure eliminalo).</p>
          <table>
            <thead><tr><th>Cliente</th><th>Quando</th><th>Conteggiati</th><th>Impegni</th></tr></thead>
            <tbody>
              {dups.map((g, i) => (
                <tr key={`${g.label}-${g.date}-${g.time}`} data-testid={`dup-group-${i}`}>
                  <td><strong>{g.label}</strong></td>
                  <td>{itD(g.date)} {g.time}</td>
                  <td>{g.counted_hours} / {g.count} {g.counted_hours > 1 && <span className="pill da_gestire">ore duplicate</span>}</td>
                  <td>
                    {g.appointments.map((a) => (
                      <button key={a.id} className="btn sm" style={{ marginRight: 6, marginBottom: 4 }} onClick={() => openEditAppt(a)} data-testid={`dup-edit-${a.id}`}>
                        {a.dur}′ {a.count_hours === false ? "(escluso)" : "(conta)"} ✎
                      </button>
                    ))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Richieste da accettare */}
      <div className="panel">
        <h3>Richieste da accettare {pending.length > 0 && <span className="nav-badge">{pending.length}</span>}</h3>
        {pending.length === 0 ? <p className="muted">Nessuna richiesta in attesa.</p> : (
          <table>
            <thead><tr><th>Quando</th><th>Cliente</th><th>Modalità</th><th>Contatti</th><th></th></tr></thead>
            <tbody>
              {pending.map((a) => (
                <tr key={a.id}>
                  <td><strong>{itD(a.date)}</strong> {a.time} ({a.dur}′)
                    {a.fuori_agenda && <div style={{ color: "#f59e0b", fontSize: 12.5 }} data-testid="appt-pending-warning">⚠️ {a.fuori_agenda}{a.alternativa_libera ? ` · libero: ${a.alternativa_libera}` : ""}</div>}
                  </td>
                  <td>{a.name}{a.company ? <div className="muted">{a.company}</div> : null}</td>
                  <td><span className="chip" style={{ background: (MODES[a.mode] || MODES.scelta).color }}>{(MODES[a.mode] || MODES.scelta).short}</span></td>
                  <td className="muted">{a.email}<br />{a.phone}</td>
                  <td style={{ whiteSpace: "nowrap" }}>
                    <button className="btn primary sm" onClick={() => accept(a.id)}>Accetta</button>{" "}
                    <button className="btn sm" onClick={() => { setPropose(a); setPropDate(a.date); setPropTime(a.time); }}>Controproponi</button>{" "}
                    <button className="btn sm" onClick={() => setOrigin(a)} data-testid={`appt-origin-btn-${a.id}`}>🔎 Da dove arriva</button>{" "}
                    <button className="btn sm" style={{ color: "#f59e0b" }} onClick={() => reject(a)} data-testid={`appt-reject-btn-${a.id}`}>✕ Rifiuta</button>{" "}
                    <button className="btn sm" style={{ color: "#f87171" }} onClick={() => removeRequest(a)} data-testid={`appt-delete-btn-${a.id}`}>🗑 Elimina</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Link prenotazione */}
      <div className="panel">
        <h3>Invia link di prenotazione</h3>
        <div className="toolbar">
          <label style={{ fontWeight: 600 }}>Modalità:</label>
          <select style={{ width: "auto" }} value={sendMode} onChange={(e) => setSendMode(e.target.value)}>
            {(cfg?.modes_enabled || ["zoom", "phone", "presenza"]).map((mk) => (
              <option key={mk} value={mk}>{MODES[mk]?.lbl}</option>
            ))}
            <option value="scelta">Lascia scegliere al cliente</option>
          </select>
          <button className="btn primary" onClick={copyLink}>Copia link</button>
        </div>
        <p className="muted">Link base: {cfg?.booking_link}</p>
      </div>

      {/* Calendario */}
      <div className="panel">
        <div className="toolbar">
          <button className="btn sm" onClick={() => navPeriod(-1)} data-testid="week-prev">‹ {view === "mese" ? "Mese prec." : "Settimana prec."}</button>
          <h3 style={{ margin: 0 }}>{periodLabel}</h3>
          <button className="btn sm" onClick={() => navPeriod(1)} data-testid="week-next">{view === "mese" ? "Mese succ." : "Settimana succ."} ›</button>
          <button className="btn sm" onClick={() => setWeekStart(startOfDay(new Date()))} data-testid="week-today">Oggi</button>
          <div className="spacer" />
          <div className="tabs" style={{ margin: 0 }}>
            <button className={view === "mese" ? "active" : ""} onClick={() => setView("mese")} data-testid="view-month">Mese</button>
            <button className={view === "settimana" ? "active" : ""} onClick={() => { setView("settimana"); setWeekStart(startOfDay(new Date())); }} data-testid="view-week">Settimana</button>
          </div>
          <button className="btn sm primary" onClick={() => { setEditId(null); setForm(emptyForm); setNewAppt(true); }} data-testid="new-appt-btn">+ Nuovo impegno</button>
        </div>
        <div className="week-recap" data-testid="week-recap">
          <span><strong>{recapAppts.length}</strong> {recapAppts.length === 1 ? "appuntamento" : "appuntamenti"}</span>
          <span className="week-recap-sep">·</span>
          <span><strong>{recapHoursLbl}</strong> {recapHours === 1 ? "ora" : "ore"} conteggiate</span>
        </div>

        {view === "mese" && (
          <div className="month-grid" data-testid="month-grid">
            {["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"].map((g) => <div key={g} className="month-head">{g}</div>)}
            {monthDays.map((d) => {
              const k = dkey(d);
              const items = dayItems(k);
              const inMonth = isCurrentMonth || d.getMonth() === weekStart.getMonth();
              return (
                <div key={k} className={"month-cell" + (k === todayKey ? " today" : "") + (inMonth ? "" : " out")} data-testid={`month-day-${k}`}>
                  <div className="month-daynum">
                    <span>{d.getDate()}</span>
                    <button className="month-add" onClick={() => openNewOnDate(k)} title="Aggiungi impegno" data-testid={`month-add-${k}`}>+</button>
                  </div>
                  {items.map((it, i) => renderItem(it, i, true))}
                </div>
              );
            })}
          </div>
        )}

        {view === "settimana" && (
        <div className="week-list">
          {weekDays.map((d) => {
            const k = dkey(d);
            const items = dayItems(k);
            return (
              <div key={k} className={"week-day" + (k === todayKey ? " today" : "")} data-testid={`week-day-${k}`}>
                <div className="week-dayhead">
                  <span>{GIORNI_FULL[d.getDay()]} <strong>{d.getDate()}</strong> {MESI[d.getMonth()].slice(0, 3)}{k === todayKey ? " · oggi" : ""}</span>
                  <button className="btn sm" onClick={() => openNewOnDate(k)} data-testid={`week-add-${k}`}>+</button>
                </div>
                <div className="week-evs">
                  {items.length === 0 && <div className="week-empty">Nessun impegno</div>}
                  {items.map((it, i) => renderItem(it, i, false))}
                </div>
              </div>
            );
          })}
        </div>
        )}
      </div>

      {/* Prossimi confermati */}
      <div className="panel">
        <h3>Prossimi appuntamenti confermati</h3>
        {confirmed.length === 0 ? <p className="muted">Nessun appuntamento confermato.</p> : (
          <table>
            <thead><tr><th>Quando</th><th>Cliente</th><th>Modalità</th><th></th></tr></thead>
            <tbody>
              {confirmed.map((a) => (
                <tr key={a.id}>
                  <td><strong>{itD(a.date)}</strong> {a.time}</td>
                  <td>{a.name}{a.company ? <span className="muted"> · {a.company}</span> : null}</td>
                  <td><span className="chip" style={{ background: (MODES[a.mode] || MODES.scelta).color }}>{(MODES[a.mode] || MODES.scelta).short}</span></td>
                  <td><button className="btn sm" onClick={() => setOrigin(a)} data-testid={`appt-origin-btn-${a.id}`}>🔎 Da dove arriva</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Promemoria automatici */}
      <div className="panel">
        <h3>Promemoria automatici</h3>
        <p className="muted">
          Al cliente vengono inviati: messaggio specifico per modalità alla conferma,
          un promemoria il giorno prima e un reminder un'ora prima con tutte le istruzioni utili
          (link Zoom, "ti chiamo io", oppure indicazioni per lo studio).
        </p>
      </div>

      {newAppt && (
        <ApptModal
          form={form} setForm={setForm}
          companies={companies} setCompanies={setCompanies}
          leads={leads} gcalConnected={gcalConnected} editId={editId}
          contact={editContact}
          onSave={saveAppt} onDelete={deleteAppt}
          onCancel={() => { setNewAppt(false); setEditId(null); setEditContact(null); }}
        />
      )}

      {icevent && (
        <IcloudEventModal icevent={icevent} onClose={() => setIcevent(null)} onCreate={createApptFromIcloud} />
      )}

      {propose && (
        <ProposalModal propose={propose} propDate={propDate} setPropDate={setPropDate}
          propTime={propTime} setPropTime={setPropTime}
          onClose={() => setPropose(null)} onSend={sendProposal} />
      )}

      {gevent && (
        <GoogleEventModal gevent={gevent} gform={gform} setGform={setGform}
          onImport={() => createApptFromGcal(gevent)}
          onClose={() => setGevent(null)} onSave={saveGevent} onDelete={deleteGevent} />
      )}
      {reviewsOpen && <ReviewRecovery onClose={() => setReviewsOpen(false)} />}
      {origin && <ApptOrigin appt={origin} onClose={() => setOrigin(null)} />}

    </div>
  );
}
